import { useMemo } from 'react'
import { parseDate, diffDays } from './ganttUtils'
import type { CommessaStrategia } from './GanttCommesse'

interface Props {
  commesse: CommessaStrategia[]
  personale: Record<string, string>
}

const NUM_WEEKS = 16

function getMonday(d: Date): Date {
  const r = new Date(d)
  const day = r.getDay()
  const diff = day === 0 ? -6 : 1 - day
  r.setDate(r.getDate() + diff)
  r.setHours(0, 0, 0, 0)
  return r
}

function pctColor(pct: number): string {
  if (pct === 0) return '#f9fafb'
  if (pct <= 30) return '#dcfce7'
  if (pct <= 60) return '#bbf7d0'
  if (pct <= 80) return '#86efac'
  if (pct <= 100) return '#4ade80'
  if (pct <= 130) return '#fbbf24'
  return '#f87171'
}


export default function HeatmapCarico({ commesse, personale }: Props) {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const startMonday = getMonday(today)

  const weeks = useMemo(() => {
    return Array.from({ length: NUM_WEEKS }, (_, i) => {
      const mon = new Date(startMonday)
      mon.setDate(mon.getDate() + i * 7)
      const fri = new Date(mon)
      fri.setDate(fri.getDate() + 4)
      return { start: mon, end: fri, label: mon.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) }
    })
  }, [startMonday])

  // Per ogni persona × settimana: giorni assegnati
  const heatmap = useMemo(() => {
    // Raccogli tutti i personale con assignments
    const personIds = new Set<string>(Object.keys(personale))
    commesse.forEach(c => c.fasi.forEach(f => {
      ;[...f.partecipanti_ids, f.responsabile_id].filter(Boolean).forEach(pid => personIds.add(pid))
    }))

    const data: Record<string, number[]> = {}
    personIds.forEach(pid => { data[pid] = new Array(NUM_WEEKS).fill(0) })

    commesse.forEach(c => {
      c.fasi.forEach(f => {
        if (!f.data_inizio_prevista || !f.data_fine_prevista) return
        const fStart = parseDate(f.data_inizio_prevista)
        const fEnd = parseDate(f.data_fine_prevista)
        const pids = [...new Set([...f.partecipanti_ids, f.responsabile_id].filter(Boolean))]

        weeks.forEach((w, wi) => {
          const overlapStart = fStart > w.start ? fStart : w.start
          const overlapEnd = fEnd < w.end ? fEnd : w.end
          if (overlapEnd < overlapStart) return
          const days = diffDays(overlapStart, overlapEnd) + 1
          pids.forEach(pid => {
            if (data[pid]) data[pid][wi] += days
          })
        })
      })
    })

    return data
  }, [commesse, personale, weeks])

  // Ordina: prima chi ha carichi, poi alfabetico
  const sortedPersons = useMemo(() => {
    return Object.keys(heatmap)
      .map(pid => ({ pid, nome: personale[pid] || 'N/D', totale: heatmap[pid].reduce((a, b) => a + b, 0) }))
      .sort((a, b) => b.totale - a.totale || a.nome.localeCompare(b.nome))
  }, [heatmap, personale])

  if (sortedPersons.length === 0) {
    return <div className="text-center text-gray-400 py-16">Nessun dato disponibile</div>
  }

  return (
    <div className="space-y-4">
      {/* Legend */}
      <div className="flex items-center gap-6 text-xs text-gray-500">
        <span className="font-medium">Occupazione settimanale (su 5 giorni lavorativi)</span>
        <div className="flex items-center gap-2">
          {[
            { color: '#f9fafb', label: '0%' },
            { color: '#86efac', label: '80%' },
            { color: '#4ade80', label: '100%' },
            { color: '#fbbf24', label: '130%' },
            { color: '#f87171', label: '>130%' },
          ].map(l => (
            <span key={l.label} className="flex items-center gap-1">
              <span className="w-4 h-4 rounded border border-gray-200 inline-block" style={{ background: l.color }} />
              {l.label}
            </span>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full text-xs border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="sticky left-0 z-10 bg-gray-50 px-4 py-2 text-left font-medium text-gray-600 border-b border-r border-gray-200 min-w-[180px]">
                Personale
              </th>
              {weeks.map((w, i) => (
                <th
                  key={i}
                  className={`px-2 py-2 text-center font-medium border-b border-gray-200 min-w-[70px] ${
                    w.start <= today && today <= w.end ? 'bg-blue-50 text-blue-700' : 'text-gray-500'
                  }`}
                >
                  {w.label}
                  {w.start <= today && today <= w.end && (
                    <div className="text-blue-500 font-bold">▼</div>
                  )}
                </th>
              ))}
              <th className="px-3 py-2 text-center font-medium text-gray-500 border-b border-l border-gray-200">
                Tot. g
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedPersons.map(({ pid, nome, totale }) => (
              <tr key={pid} className="hover:bg-gray-50 group">
                <td className="sticky left-0 z-10 bg-white group-hover:bg-gray-50 px-4 py-1.5 border-b border-r border-gray-100 font-medium text-gray-700 truncate max-w-[180px]">
                  {nome}
                </td>
                {heatmap[pid].map((days, wi) => {
                  const pct = (days / 5) * 100
                  return (
                    <td
                      key={wi}
                      className="px-1 py-1.5 border-b border-gray-100 text-center"
                      style={{ background: pctColor(pct) }}
                      title={`${nome} — sett. ${weeks[wi].label}: ${days}g (${Math.round(pct)}%)`}
                    >
                      <span className={`font-medium ${pct > 100 ? 'text-red-700' : pct > 80 ? 'text-green-800' : 'text-gray-500'}`}>
                        {days > 0 ? `${days}g` : ''}
                      </span>
                    </td>
                  )
                })}
                <td className="px-3 py-1.5 border-b border-l border-gray-100 text-center font-semibold text-gray-600">
                  {totale > 0 ? totale : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
